import React, { useState, useMemo } from 'react';
import {
    McDialog,
    McButton,
    McInput,
    McIcon,
} from '@maersk-global/mds-react-wrapper/components-core';

/**
 * Tool Definition — the single source of truth for all tools available to AI Agents.
 *
 * To add a NEW tool:
 *   1. Add an entry to AVAILABLE_TOOLS below (set status: 'available' when ready)
 *   2. If it needs config, add configFields[] and set requiresConfig: true
 *   3. Create the backend @Tool class in agent-service/tools/
 *   4. Wire it in AgentExecutionService.executeConversationalAgent()
 *   5. Add Flyway migration if the tool needs DB tables
 *
 * To add a COMING SOON placeholder:
 *   Just add an entry with status: 'coming_soon' — the UI shows it greyed out.
 */
export interface ToolConfigField {
    key: string;
    label: string;
    type: 'text' | 'password' | 'select';
    placeholder?: string;
    options?: { label: string; value: string }[];
    defaultValue?: string;
    helpText?: string;
}

export interface ToolDefinition {
    id: string;
    label: string;
    description: string;
    icon: string;
    category: string;
    /** If true, shows config fields in the panel */
    requiresConfig?: boolean;
    /** Declarative config fields rendered automatically by NodeConfigPanel */
    configFields?: ToolConfigField[];
    /** 'available' = ready to use, 'coming_soon' = greyed-out placeholder, 'beta' = usable with badge */
    status?: 'available' | 'coming_soon' | 'beta';
    /** Short doc string shown in the tool details panel */
    docUrl?: string;
}

// ─── Tool catalog ────────────────────────────────────────────────────────────
// Categories are auto-grouped in the dialog. Order within a category matters.
export const AVAILABLE_TOOLS: ToolDefinition[] = [
    // ── Terminals ─────────────────────────────────────
    { id: 'listTerminals', label: 'List Terminals', description: 'List all available APM Terminal codes', icon: 'list', category: 'Terminals', status: 'beta' },
    { id: 'getTerminalConfigs', label: 'Terminal Configs', description: 'Get timezone configs for terminals', icon: 'gear', category: 'Terminals', status: 'beta' },
    { id: 'getRegions', label: 'Get Regions', description: 'Get regions (EMEA/NAM/APAC/LATM) and terminals', icon: 'globe', category: 'Terminals', status: 'beta' },

    // ── Queries ───────────────────────────────────────
    { id: 'queryBuilder', label: 'Database Schema', description: 'Read DB table schemas and find relevant tables for SQL generation', icon: 'diagram-project', category: 'Queries', status: 'available' },
    { id: 'listQueries', label: 'List Queries', description: 'List all saved SQL queries', icon: 'database', category: 'Queries', status: 'beta' },
    { id: 'searchQueries', label: 'Search Queries', description: 'Search queries by keyword', icon: 'magnifying-glass', category: 'Queries', status: 'beta' },
    { id: 'executeQuery', label: 'Execute Query', description: 'Execute a saved query on a terminal', icon: 'play', category: 'Queries', status: 'beta' },
    { id: 'executeRawSql', label: 'Execute Raw SQL', description: 'Run raw SQL on a terminal', icon: 'code', category: 'Queries', status: 'beta' },

    // ── Platform ──────────────────────────────────────
    { id: 'listApplications', label: 'List Applications', description: 'List registered applications', icon: 'layer-group', category: 'Platform', status: 'beta' },
    { id: 'httpGet', label: 'HTTP GET', description: 'Make GET requests to external APIs', icon: 'arrow-down-to-line', category: 'Platform', status: 'beta' },
    { id: 'httpPost', label: 'HTTP POST', description: 'Make POST requests to external APIs', icon: 'arrow-up-from-line', category: 'Platform', status: 'beta' },

    // ── AI ────────────────────────────────────────────
    {
        id: 'memory',
        label: 'Memory',
        description: 'Remember information across conversations (preferences, facts, context)',
        icon: 'bookmark',
        category: 'AI',
        requiresConfig: true,
        status: 'beta',
        configFields: [
            {
                key: 'storageType',
                label: 'Storage Type',
                type: 'select',
                defaultValue: 'inmemory',
                options: [
                    { label: '⚡ In-Memory (POD) — fast, lost on restart', value: 'inmemory' },
                    { label: '🗄️ PostgreSQL — persistent, survives restarts', value: 'postgres' },
                ],
                helpText: 'In-Memory is best for session scratch-pad. PostgreSQL is best for long-term user preferences.',
            },
        ],
    },

    // ── Integrations ──────────────────────
    {
        id: 'github',
        label: 'GitHub Integration',
        description: 'Read code, create branches, commit files, open Pull Requests, list/review PRs, and add review comments',
        icon: 'github',
        category: 'Integrations',
        requiresConfig: true,
        status: 'beta',
        configFields: [
            { key: 'repository', label: 'Repository (owner/repo)', type: 'text', placeholder: 'e.g. maersk-global/apmt-code' },
            { key: 'branch', label: 'Base Branch', type: 'text', placeholder: 'e.g. main' },
            { key: 'token', label: 'Personal Access Token', type: 'password', placeholder: 'ghp_........................' },
        ],
    },

    // ── Integrations (beta — newly implemented) ────────────
    {
        id: 'sharepoint',
        label: 'SharePoint',
        description: 'Search, read, and list documents in SharePoint Online sites and libraries',
        icon: 'folder',
        category: 'Integrations',
        requiresConfig: true,
        status: 'beta',
        configFields: [
            { key: 'tenantId', label: 'Azure AD Tenant ID', type: 'text', placeholder: 'Azure AD Tenant ID' },
            { key: 'clientId', label: 'App Registration Client ID', type: 'text', placeholder: 'Azure AD App Client ID' },
            { key: 'clientSecret', label: 'Client Secret', type: 'password', placeholder: 'Azure AD App Secret' },
        ],
    },
    {
        id: 'confluence',
        label: 'Confluence',
        description: 'Search and read Confluence wiki pages, spaces, and documentation',
        icon: 'book',
        category: 'Integrations',
        requiresConfig: true,
        status: 'beta',
        configFields: [
            { key: 'baseUrl', label: 'Confluence Base URL', type: 'text', placeholder: 'https://your-domain.atlassian.net' },
            { key: 'accountEmail', label: 'Atlassian Account Email', type: 'text', placeholder: 'your.name@maersk.com' },
            { key: 'apiToken', label: 'API Token', type: 'password', placeholder: 'Atlassian API Token' },
        ],
    },
    {
        id: 'jira',
        label: 'Jira',
        description: 'Create, search, and update Jira issues and projects',
        icon: 'clipboard-check',
        category: 'Integrations',
        requiresConfig: true,
        status: 'beta',
        configFields: [
            { key: 'baseUrl', label: 'Jira Base URL', type: 'text', placeholder: 'https://your-domain.atlassian.net' },
            { key: 'accountEmail', label: 'Atlassian Account Email', type: 'text', placeholder: 'your.name@maersk.com' },
            { key: 'apiToken', label: 'API Token', type: 'password', placeholder: 'Atlassian API Token' },
        ],
    },

    // ── Communication (beta — newly implemented) ──────────
    {
        id: 'outlook',
        label: 'Outlook Mail',
        description: 'Read, search, send, and reply to emails via Microsoft Graph',
        icon: 'envelope',
        category: 'Communication',
        requiresConfig: true,
        status: 'beta',
        configFields: [
            { key: 'tenantId', label: 'Azure AD Tenant ID', type: 'text', placeholder: 'Tenant ID' },
            { key: 'clientId', label: 'App Client ID', type: 'text', placeholder: 'Azure AD App Client ID' },
            { key: 'clientSecret', label: 'Client Secret', type: 'password', placeholder: 'Azure AD App Secret' },
        ],
    },
    {
        id: 'outlookCalendar',
        label: 'Outlook Calendar',
        description: 'Create, read, update, and cancel calendar events via Microsoft Graph',
        icon: 'calendar',
        category: 'Communication',
        requiresConfig: true,
        status: 'beta',
        configFields: [
            { key: 'tenantId', label: 'Azure AD Tenant ID', type: 'text', placeholder: 'Tenant ID' },
            { key: 'clientId', label: 'App Client ID', type: 'text', placeholder: 'Azure AD App Client ID' },
            { key: 'clientSecret', label: 'Client Secret', type: 'password', placeholder: 'Azure AD App Secret' },
        ],
    },
    {
        id: 'teams',
        label: 'Microsoft Teams',
        description: 'Send messages, list channels, read conversations via Microsoft Graph',
        icon: 'message',
        category: 'Communication',
        requiresConfig: true,
        status: 'beta',
        configFields: [
            { key: 'tenantId', label: 'Azure AD Tenant ID', type: 'text', placeholder: 'Tenant ID' },
            { key: 'clientId', label: 'App Client ID', type: 'text', placeholder: 'Azure AD App Client ID' },
            { key: 'clientSecret', label: 'Client Secret', type: 'password', placeholder: 'Azure AD App Secret' },
        ],
    },
];

interface AddToolDialogProps {
    open: boolean;
    onClose: () => void;
    onAddTools: (toolIds: string[]) => void;
    existingTools: string[];
}

export const AddToolDialog: React.FC<AddToolDialogProps> = ({
    open,
    onClose,
    onAddTools,
    existingTools
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTools, setSelectedTools] = useState<string[]>([]);

    React.useEffect(() => {
        if (open) {
            setSearchTerm('');
            setSelectedTools([]);
        }
    }, [open]);

    const filteredTools = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase();
        return AVAILABLE_TOOLS.filter(tool => {
            // Filter out tools that are already added (but keep coming_soon visible)
            if (existingTools.includes(tool.id) && tool.status !== 'coming_soon') {
                return false;
            }
            return tool.label.toLowerCase().includes(lowerSearch) ||
                tool.description.toLowerCase().includes(lowerSearch) ||
                tool.category.toLowerCase().includes(lowerSearch);
        });
    }, [searchTerm, existingTools]);

    // Group tools by category
    const toolsByCategory = useMemo(() => {
        const grouped: Record<string, ToolDefinition[]> = {};
        for (const tool of filteredTools) {
            if (!grouped[tool.category]) {
                grouped[tool.category] = [];
            }
            grouped[tool.category].push(tool);
        }
        return grouped;
    }, [filteredTools]);

    if (!open) return null;

    return (
        <McDialog
            open={open}
            closed={onClose}
            heading="Search and Add Tools"
            width="600px"
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '400px', gap: '16px', position: 'relative' }}>
                <McInput
                    placeholder="Search tools by name or description..."
                    value={searchTerm}
                    input={(e: any) => setSearchTerm(e.target.value)}
                    style={{ width: '100%' }}
                >
                    <McIcon icon="magnifying-glass" slot="prefix" />
                </McInput>

                <div style={{ flexGrow: 1, overflowY: 'auto', border: '1px solid var(--mds-color-neutral-20)', borderRadius: '4px', padding: '0' }}>
                    {Object.keys(toolsByCategory).length === 0 ? (
                        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--mds-color-neutral-60)' }}>
                            No tools found. Try a different search term.
                        </div>
                    ) : (
                        Object.entries(toolsByCategory).map(([category, tools]) => (
                            <div key={category} style={{ borderBottom: '1px solid var(--mds-color-neutral-10)' }}>
                                <div style={{
                                    padding: '8px 16px',
                                    backgroundColor: 'var(--mds-color-neutral-05)',
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem',
                                    color: 'var(--mds-color-neutral-70)'
                                }}>
                                    {category}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    {tools.map(tool => {
                                        const isComingSoon = tool.status === 'coming_soon';
                                        const isBeta = tool.status === 'beta';
                                        return (
                                            <div
                                                key={tool.id}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '12px 16px',
                                                    borderBottom: '1px solid var(--mds-color-neutral-10)',
                                                    cursor: isComingSoon ? 'default' : 'pointer',
                                                    transition: 'background-color 0.2s',
                                                    opacity: isComingSoon ? 0.55 : 1,
                                                }}
                                                onMouseEnter={(e) => { if (!isComingSoon) e.currentTarget.style.backgroundColor = 'var(--mds-color-brand-light-blue)'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                onClick={() => { 
                                                    if (!isComingSoon) {
                                                        setSelectedTools(prev => prev.includes(tool.id) ? prev.filter(id => id !== tool.id) : [...prev, tool.id]);
                                                    } 
                                                }}
                                            >
                                                <div style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '8px',
                                                    backgroundColor: isComingSoon ? 'var(--mds-color-neutral-05)' : 'var(--mds-color-neutral-10)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginRight: '16px'
                                                }}>
                                                    <McIcon icon={tool.icon} />
                                                </div>
                                                <div style={{ flexGrow: 1 }}>
                                                    <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {tool.label}
                                                        {isComingSoon && (
                                                            <span style={{
                                                                fontSize: '10px', fontWeight: 600, padding: '2px 8px',
                                                                borderRadius: '10px', backgroundColor: '#FEF3C7', color: '#92400E',
                                                                textTransform: 'uppercase', letterSpacing: '0.5px',
                                                            }}>Coming Soon</span>
                                                        )}
                                                        {isBeta && (
                                                            <span style={{
                                                                fontSize: '10px', fontWeight: 600, padding: '2px 8px',
                                                                borderRadius: '10px', backgroundColor: '#DBEAFE', color: '#1E40AF',
                                                                textTransform: 'uppercase', letterSpacing: '0.5px',
                                                            }}>Beta</span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontSize: '0.85rem', color: 'var(--mds-color-neutral-60)' }}>{tool.description}</div>
                                                </div>
                                                {isComingSoon
                                                    ? <span style={{ fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic' }}>Planned</span>
                                                    : <input 
                                                        type="checkbox" 
                                                        readOnly 
                                                        checked={selectedTools.includes(tool.id)} 
                                                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#2563EB' }} 
                                                      />
                                                }
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>
                
                {/* Footer fixed at bottom */}
                <div style={{ 
                    marginTop: 'auto', 
                    paddingTop: '16px', 
                    borderTop: '1px solid var(--mds-color-neutral-20)', 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: '12px' 
                }}>
                    <McButton variant="outlined" onClick={onClose}>Cancel</McButton>
                    <McButton 
                        disabled={selectedTools.length === 0} 
                        onClick={() => onAddTools(selectedTools)}
                    >
                        Add Selected ({selectedTools.length})
                    </McButton>
                </div>
            </div>
        </McDialog>
    );
};
